<script>
import ScrollableActions from '@maison/components/scrollable-actions/ScrollableActions.vue';
import SearchResultsTab from '@maison/components/search/SearchResultsTab';
import ProductCard from '@maison/components/product/ProductCard.vue';
import { TAB_PRODUCTS } from '@maison/helpers/search-helper';
import MixedGridPagePushItem from '@maison/components/mixedGrid/MixedGridPagePushItem.vue';
import { uniqueId } from 'lodash-es';
import AlgoliaSearch, { IndexNames } from '@module/Algolia/resources/js/search/AlgoliaSearch';
import { mapActions } from 'vuex';
import AlgoliaMixedGridFilters from '@maison/components/algolia/AlgoliaMixedGridFilters.vue';
import { algoliaTracking } from '@module/Algolia/resources/js/insights/helpers';
import searchResultsTabMixin from '@phoenix/mixins/searchResultsTabMixin';
import searchResultsTrackingMixin from '@maison/mixins/searchResultsTrackingMixin';
import { rescue } from '@phoenix/helpers/common-helper';

export default {
  components: {
    AlgoliaMixedGridFilters,
    MixedGridPagePushItem,
    ScrollableActions,
    SearchResultsTab,
    ProductCard,
  },

  mixins: [searchResultsTabMixin, searchResultsTrackingMixin],

  inject: {
    scrollableActionsWidget: {
      default: () => ({}),
    },
  },

  data() {
    return {
      tab: TAB_PRODUCTS,
      uniqueId: uniqueId('search-results-products'),
      hasFilters: AlgoliaSearch.isEnabled(),
      filtersPanelId: 'search-products-filters-panel',
    };
  },

  computed: {
    suggestedCollection() {
      return this.tabResult.suggestedCollection;
    },

    suggestedCollectionCta() {
      return {
        text: this.$t('Accessibility.product.discoverMore'),
        target: {
          type: 'page',
          link: this.suggestedCollection.link,
        },
      };
    },

    shouldDisplayFilters() {
      return this.hasFilters && this.limit === 0;
    },

    filters() {
      return JSON.parse(JSON.stringify(this.tabResult.filters));
    },

    activeFilters() {
      return this.tabResult.activeFilters;
    },

    currentSort() {
      return this.tabResult.currentSort;
    },

    isFiltered() {
      return Object.keys(this.activeFilters).length > 0;
    },
  },

  methods: {
    ...mapActions('search', [
      'changeCurrentTab',
      'loadData',
      'setSortOption',
      'setActiveFilters',
      'setHasInteractedWithResults',
    ]),

    async onSortChanged(sortOption) {
      this.setSortOption({ tab: this.tab, sortOption });
      await this.loadData({ clearData: true, tab: this.tab });
    },

    async onFiltersChanged(filters) {
      this.setHasInteractedWithResults();
      this.setActiveFilters({ tab: this.tab, filters });
      await this.loadData({ clearData: true, tab: this.tab });

      rescue(() => {
        const formattedFilters = Object.entries(filters)
          .map(([category, values]) => `${category};${Array.isArray(values) ? values.join(';') : values}`)
          .join('|');

        window.tracking.pushEvent(window.tracking.getSearchFilterViewResultsTracking(this.query, formattedFilters));
      });
    },

    onProductClick(product, index) {
      this.setHasInteractedWithResults();
      algoliaTracking(product, index, IndexNames.Products);

      this.sendSearchClickTracking(this.query, product.url, index, 'product');
    },
  },
};
</script>

<template>
  <search-results-tab :tab="tab" :limit="limit" class="section--overflow-visible">
    <section v-if="scrollableActionsWidget && isTabActive" class="section pb-base">
      <div class="container">
        <scrollable-actions v-bind="scrollableActionsWidget" />
      </div>
    </section>

    <algolia-mixed-grid-filters
      v-if="shouldDisplayFilters"
      class="filters-bar--search-results"
      :unique-id="uniqueId"
      :filters="filters"
      :panel-id="filtersPanelId"
      :should-display-products-count="false"
      @on-sort-changed="onSortChanged"
      @on-filters-changed="onFiltersChanged"
    />

    <div class="container">
      <div class="products__grid">
        <div class="mixed-grid">
          <mixed-grid-page-push-item
            v-if="suggestedCollection"
            v-show="isTabActive && !isFiltered"
            :title="suggestedCollection.title"
            :cta="suggestedCollectionCta"
            :size="2"
            :item-id="suggestedCollection.id"
            :image="suggestedCollection.rokkaImg"
          />
          <product-card
            v-for="(product, index) in items"
            :key="product.modelReference"
            v-bind="product"
            enable-slider
            @click="onProductClick(product, index)"
          />
        </div>
      </div>
    </div>
  </search-results-tab>
</template>
